import { defineStore } from 'pinia';
import { ref } from 'vue';

import { fetchCustomProductDetail } from '@/api/customProducts';
import { DetailProductsByCustomProductKeyProjectionResult } from '@/graphql/customProducts';
import { SupportedCustomProductKey } from '@/lib/product';
import { reportError } from '@/utils/reportError';

export const useCustomProductStore = (customProductKey: SupportedCustomProductKey) =>
  defineStore(`pdp:${customProductKey.replace(' ', '-').toLowerCase()}`, () => {
    const products = ref<DetailProductsByCustomProductKeyProjectionResult[]>([]);
    const isPending = ref(false);
    const error = ref<Error>();

    const loadProducts = async () => {
      isPending.value = true;
      error.value = undefined;
      try {
        const [results, { HTMLCleaner }] = await Promise.all([
          fetchCustomProductDetail({
            filter: `variants.attributes.customProduct.key:"${customProductKey}"`,
            limit: 20,
          }),
          import('@/utils/html_cleaner'),
        ]);

        products.value = results.map((result) => ({
          ...result,
          description: HTMLCleaner.clean(result.description),
        }));
      } catch (err) {
        const errorMessage = reportError(
          err,
          'Failed to fetch custom products by custom product key',
          {
            customProductKey,
          },
        );
        error.value = err instanceof Error ? err : new Error(errorMessage);
      } finally {
        isPending.value = false;
      }
    };

    return {
      error,
      isPending,
      loadProducts,
      products,
    };
  })();
